import * as React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import FormManager from "../../../components/form/FormManager";
import FormRow from "../../../components/form/FormRow";
import InputNumber from "../../../components/input/InputNumber";
import InputTextArea from "../../../components/input/InputTextArea";
import DateTimePicker from "../../../components/datetime/DateTimePicker";
import ClientList from "../../../components/select/ClientList";
import AdminContainerMigrate from "../../../components/container/AdminContainerMigrate";
import LoadingButtonWithConfirm from "../../../components/button/LoadingButtonWithConfirm";
import NatTime from "../../../components/text/NatTime";
import { addChildShiftPOST } from "../../../api/apiEndpoints";
import { clientSelectorById } from "../../../poll/selectors";
import {
	convertDateToAPIDate,
	differenceInMinutes,
	parseAPIDate,
	offsetApiTime
} from "../../../lib/dateUtils/dateUtils";
import { shiftFeePayModifierForDate } from "./shiftFeeUtils";

// "Add a day to this block": one more day on an existing multiday block -
// a one-off block or one occurrence of a repeating series (stage 1: this
// block only; the series is not changed). The day is written by the server
// with both links a block needs, and the block's span follows.
class AddChildShift extends React.Component {
	constructor(props) {
		super(props);
		// Default the new day to the day after the block's last day, same
		// start hour and length as the block's last day.
		const days = (props.shift.Multiday || []).slice().sort((a, b) => {
			return parseAPIDate(a.StartTime) - parseAPIDate(b.StartTime);
		});
		const last = days[days.length - 1];
		let start = parseAPIDate(props.shift.EndTime);
		let length = 240;
		let clientId = undefined;
		let payRate = 0;
		if (last) {
			const lastStart = parseAPIDate(last.StartTime);
			start = new Date(lastStart.getTime() + 24 * 60 * 60 * 1000);
			length = last.ShiftLength || differenceInMinutes(parseAPIDate(last.EndTime), parseAPIDate(last.StartTime));
			clientId = last.ClientId;
			payRate = last.PayRate || 0;
		}
		this.initialStart = convertDateToAPIDate(start);
		this.initialEnd = offsetApiTime(this.initialStart, length);
		this.initialClientId = clientId;
		this.initialPayRate = payRate;
		this.state = { saveError: null };
	}

	pays = (startApi, endApi, payRate) => {
		// date-fns: later date first, or the minutes come out negative and the
		// pay clamps to 0 (every added day showed Shift pay 0 until 4 Nov 2026).
		const minutes = differenceInMinutes(parseAPIDate(endApi), parseAPIDate(startApi));
		const shiftPay = Number(((Math.max(minutes, 0) / 60) * Number(payRate || 0)).toFixed(2));
		const feePay = Number((shiftPay * shiftFeePayModifierForDate(parseAPIDate(startApi))).toFixed(2));
		return { ShiftPay: shiftPay, FeePay: feePay };
	};

	render() {
		const block = this.props.shift;
		return (
			<FormManager
				validateOnLoad={true}
				initialFormValues={{
					ClientId: this.initialClientId,
					StartTime: this.initialStart,
					EndTime: this.initialEnd,
					PayRate: this.initialPayRate,
					...this.pays(this.initialStart, this.initialEnd, this.initialPayRate),
					Notes: ""
				}}
				handleValidation={v => {
					v.required({ fieldNames: ["ClientId", "StartTime", "EndTime"] });
					v.apiDateGreaterThan({ lessThanFieldName: "StartTime", greaterThanFieldName: "EndTime" });
				}}
				handleSubmit={({ formValues, handleSubmitFail, handleSubmitSuccess }) => {
					this.setState({ saveError: null });
					const client = this.props.clientSelectorById[formValues.ClientId];
					addChildShiftPOST({
						ShiftId: block.Id,
						ClientId: formValues.ClientId,
						StartTime: formValues.StartTime,
						EndTime: formValues.EndTime,
						PayRate: formValues.PayRate,
						ShiftPay: formValues.ShiftPay,
						FeePay: formValues.FeePay,
						Notes: formValues.Notes,
						Requirements: client ? client.Requirements : null
					})
						.then(() => {
							handleSubmitSuccess();
							this.props.handleCloseModal();
						})
						.catch(() => {
							handleSubmitFail();
							this.setState({
								saveError: "The day was not added. Refresh the page to check before trying again."
							});
						});
				}}
			>
				{({ formValues, handleOnChange, handleSetFormValues, handleSubmit, formValidation, submitEnabled, submitting }) => {
					const handleTimeOrRate = e => {
						const next = { ...formValues, [e.target.name]: e.target.value };
						if (e.target.name === "StartTime") {
							// The finish follows the start, keeping the day's length, as
							// the edit wizard does: set the date once, then the two times.
							// A day over midnight: set the start, then push the finish
							// to the next day (Nat, 4 Nov 2026).
							const length = differenceInMinutes(parseAPIDate(formValues.EndTime), parseAPIDate(formValues.StartTime));
							next.EndTime = offsetApiTime(e.target.value, Math.max(length, 0));
						}
						handleSetFormValues({ ...next, ...this.pays(next.StartTime, next.EndTime, next.PayRate) });
					};
					return (
						<div>
							<p>
								Adding a day to the block{" "}
								<b>
									<NatTime startTime={block.StartTime} endTime={block.EndTime} />
								</b>
								. The block's dates will stretch to include it; the booking stays as it is, and a repeating series' other weeks are not changed.
							</p>
							<FormRow
								label={"Venue"}
								value={<ClientList name="ClientId" value={formValues.ClientId} onChange={handleOnChange} />}
							/>
							<FormRow
								label={"Day starts"}
								value={
									<DateTimePicker
										name="StartTime"
										value={formValues.StartTime}
										onChange={handleTimeOrRate}
										validationMessages={formValidation.StartTime}
									/>
								}
							/>
							<FormRow
								label={"Day ends"}
								value={
									<DateTimePicker
										name="EndTime"
										value={formValues.EndTime}
										onChange={handleTimeOrRate}
										validationMessages={formValidation.EndTime}
									/>
								}
							/>
							<FormRow
								label={"Pay rate"}
								value={
									<InputNumber name="PayRate" value={formValues.PayRate} onChange={handleTimeOrRate} validationMessages={formValidation.PayRate} />
								}
							/>
							<FormRow
								label={"Shift pay"}
								value={
									<InputNumber name="ShiftPay" value={formValues.ShiftPay} onChange={handleOnChange} validationMessages={formValidation.ShiftPay} />
								}
							/>
							<FormRow
								label={"Fee"}
								value={
									<InputNumber name="FeePay" value={formValues.FeePay} onChange={handleOnChange} validationMessages={formValidation.FeePay} />
								}
							/>
							<FormRow
								label={"Notes"}
								value={<InputTextArea name="Notes" value={formValues.Notes} onChange={handleOnChange} validationMessages={formValidation.Notes} />}
							/>
							{this.state.saveError && <p className="color-fail">{this.state.saveError}</p>}
							<AdminContainerMigrate
								align={"right"}
								buttons={[
									<LoadingButtonWithConfirm
										key="add"
										className="btn btn-primary"
										onClick={handleSubmit}
										loading={submitting}
										disabled={!submitEnabled}
										confirmMessage="Add this day to the block?"
									>
										Add this day
									</LoadingButtonWithConfirm>
								]}
							/>
						</div>
					);
				}}
			</FormManager>
		);
	}
}

AddChildShift.propTypes = {
	shift: PropTypes.object.isRequired,
	handleCloseModal: PropTypes.func.isRequired,
	clientSelectorById: PropTypes.object
};

const mapStateToProps = state => {
	return { clientSelectorById: clientSelectorById(state) };
};

export default connect(mapStateToProps)(AddChildShift);
